import { useMemo } from "react";
import { SearchInput } from "../../common/forms/SearchInput/SearchInput";
import { LucideIcon } from "../../common/icon/LucideIcon";
import TableView from "../../common/panels/TableView/TableView";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import { EntityTable } from "../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { EntityFilterIndicator } from "../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { useTabStore } from "../../common/tables/Tabs/useTabStore";
import { TableTabsDict } from "../../common/tables/Tabs/TableTabsTypes";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  UseEntityTableDefaultProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
  UseITypedEntityTableProps,
} from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../common/entity/EntityInterfaces";
import { toUppercase } from "../../common/helperfunctions/stringFunctions";
import { CustomImport, CustomImportFilters } from "../../api/CustomImports";
import { ReloadMetadataTemplateConfigurationsButton } from "../CustomImportsDetail";
import { EntityOrderBy, InferSortByOptions } from "../../api/GenericTypes";

export const switchCustomImportsDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<CustomImportFilters>>[] | null | undefined
): SortState<InferSortByOptions<CustomImportFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;
  return [
    {
      sortDirection: "ASC",
      headerId: "default-name",
      property: "NAME",
    },
  ];
};

const defaultCustomImportsFilters: CustomImportFilters = { sortBy: [{ property: "NAME", sortDirection: "ASC" }] };

export const useCustomImportsTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"customImports">) => {
  const defaults: ColumnsSettings<CustomImport> = useMemo(
    () => ({
      "default-id": { pos: 0, active: true, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
    }),
    [fieldLabels.id, fieldLabels.name]
  );

  const tabStoreDefaults: TableTabsDict<CustomImport, CustomImportFilters, any> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultCustomImportsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
    }),
    []
  );
  return { defaults, tabStoreDefaults };
};

export const useCustomImportsTableColumns = ({
  entityConstants,
  fieldLabels,
  defaults,
  filters,
  sort,
  setSort,
}: UseITypedEntityTableProps<"customImports">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<CustomImport> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ color: "var(--gray-400)" }}>{row.id}</span>
            </div>
          </div>
        ),
        width: 300,
        minWidth: 100,
        align: "left",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.name}
              </span>
            </div>
          </div>
        ),
        minWidth: 100,
        width: 500,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
    ];
    return headers;
  }, [entityConstants.icon, fieldLabels.id, fieldLabels.name, setSort, sort]);

  return { columns };
};
export const CustomImportsTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityTableProps<"customImports">) => {
  const { defaults, tabStoreDefaults } = useCustomImportsTableDefaults({ fieldLabels });

  const { resultsCount, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<CustomImport>();

  const { filters, forcedFilters, columnSetting, columnWidths, dispatchTabStore, currentTab, tabsLoading } =
    useTabStore<CustomImport, CustomImportFilters, any>({
      resource: entityConstants.resource,
      defaults: tabStoreDefaults,
    });

  const { functionRef, sort, setSort, searchValue, setSearchValue } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchCustomImportsDefaultSortState,
    dispatchTabStore,
  });

  const { columns } = useCustomImportsTableColumns({
    entityConstants,
    fieldLabels,
    defaults,
    filters,
    sort,
    setSort,
  });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <div>
            {toUppercase(entityConstants.entitySingular)}{" "}
            {typeof resultsCount === "number" && <span className="badge">{resultsCount}</span>}
          </div>
        </TableView.Head.Label>
        <TableView.Head.Controls>
          <ReloadMetadataTemplateConfigurationsButton filters={memoizedFilters} />
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Content>
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<CustomImport, CustomImportFilters> filters={filters} excludeFilters={{}} />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
            </EntityTable.Controls>
            <EntityTable.Body<CustomImport, CustomImportFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
